<template>
  <div class="rich-editor">
    <a-alert
      v-if="!hasTinyMceApiKey"
      class="license-alert"
      type="warning"
      show-icon
      message="TinyMCE 尚未配置 API Key"
      description="当前仅供本地预览。上线前请在 apps/admin/.env 中配置 VITE_TINYMCE_API_KEY。"
    />
    <Editor v-model="html" :api-key="tinyMceApiKey" :init="editorConfig" @update:model-value="saveHtml" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import Editor from '@tinymce/tinymce-vue';
import { cmsRichTextHtml, type CmsContentBlock, type CmsContentSection, type CmsRichTextDocument, type CmsRichTextNode } from 'shared';
import { useCmsContentStore } from '@/stores/cms-content.store';

const model = defineModel<CmsContentSection[]>({ required: true });
const store = useCmsContentStore();
const tinyMceApiKey = import.meta.env.VITE_TINYMCE_API_KEY || 'no-api-key';
const hasTinyMceApiKey = tinyMceApiKey !== 'no-api-key';
let writingFromEditor = false;

function legacyNode(block: CmsContentBlock): CmsRichTextNode[] {
  if (block.type === 'richText') return block.content.content;
  if (block.type === 'paragraph') return [{ type: 'paragraph', content: [{ type: 'text', text: block.text }] }];
  if (block.type === 'bullets') return [{ type: 'bulletList', content: block.items.map((text) => ({ type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text }] }] })) }];
  if (block.type === 'steps') return [{ type: 'orderedList', content: block.items.map((item) => ({ type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: `${item.title}：${item.text}` }] }] })) }];
  if (block.type === 'note') return [{ type: 'blockquote', content: [{ type: 'paragraph', content: [{ type: 'text', text: `${block.title}：${block.text}` }] }] }];
  if (block.type === 'links') return [{ type: 'bulletList', content: block.items.map((item) => ({ type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: item.label, marks: [{ type: 'link', attrs: { href: item.to } }] }] }] })) }];
  return [{ type: 'image', attrs: { src: `/api/v1/content/assets/${block.assetId}`, alt: block.alt } }];
}

function documentOf(sections: CmsContentSection[]): CmsRichTextDocument {
  return {
    type: 'doc',
    content: sections.flatMap((section) => [
      { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: section.title }] },
      ...section.blocks.flatMap(legacyNode),
    ]),
  };
}

function safeUrl(value: string | null): string | undefined {
  if (!value) return undefined;
  const url = value.trim();
  return /^(https?:\/\/|mailto:|\/|#)/i.test(url) ? url : undefined;
}

function markFor(element: HTMLElement): NonNullable<CmsRichTextNode['marks']>[number] | undefined {
  const tag = element.tagName;
  if (tag === 'STRONG' || tag === 'B') return { type: 'bold' };
  if (tag === 'EM' || tag === 'I') return { type: 'italic' };
  if (tag === 'U') return { type: 'underline' };
  if (tag === 'S' || tag === 'STRIKE') return { type: 'strike' };
  if (tag === 'CODE' && element.parentElement?.tagName !== 'PRE') return { type: 'code' };
  if (tag === 'A') {
    const href = safeUrl(element.getAttribute('href'));
    return href ? { type: 'link', attrs: { href } } : undefined;
  }
  return undefined;
}

function richNodes(node: Node, marks: NonNullable<CmsRichTextNode['marks']> = []): CmsRichTextNode[] {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || '';
    return text ? [{ type: 'text', text, ...(marks.length ? { marks } : {}) }] : [];
  }
  if (!(node instanceof HTMLElement)) return [];

  const mark = markFor(node);
  const nextMarks = mark ? [...marks, mark] : marks;
  const content = Array.from(node.childNodes).flatMap((child) => richNodes(child, nextMarks));
  const tag = node.tagName;
  if (tag === 'P' || tag === 'DIV') return [{ type: 'paragraph', content }];
  if (/^H[1-3]$/.test(tag)) return [{ type: 'heading', attrs: { level: Number(tag[1]) }, content }];
  if (tag === 'UL') return [{ type: 'bulletList', content }];
  if (tag === 'OL') return [{ type: 'orderedList', content }];
  if (tag === 'LI') return [{ type: 'listItem', content: content.length ? content : [{ type: 'paragraph' }] }];
  if (tag === 'BLOCKQUOTE') return [{ type: 'blockquote', content }];
  if (tag === 'PRE') return [{ type: 'codeBlock', content }];
  if (tag === 'TABLE') return [{ type: 'table', content }];
  if (tag === 'TR') return [{ type: 'tableRow', content }];
  if (tag === 'TH') return [{ type: 'tableHeader', content }];
  if (tag === 'TD') return [{ type: 'tableCell', content }];
  if (tag === 'BR') return [{ type: 'hardBreak' }];
  if (tag === 'HR') return [{ type: 'horizontalRule' }];
  if (tag === 'IMG') {
    const src = safeImageUrl(node.getAttribute('src'));
    return src ? [{ type: 'image', attrs: { src, alt: node.getAttribute('alt') || '' } }] : [];
  }
  return content;
}

function safeImageUrl(value: string | null): string | undefined {
  if (!value) return undefined;
  const url = value.trim();
  return /^(https:\/\/|\/)/i.test(url) ? url : undefined;
}

function documentFromHtml(value: string): CmsRichTextDocument {
  const parsed = new DOMParser().parseFromString(value, 'text/html');
  const content = Array.from(parsed.body.childNodes).flatMap((node) => richNodes(node));
  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
}

const html = ref(cmsRichTextHtml(documentOf(model.value)));

function saveHtml(value: string): void {
  writingFromEditor = true;
  model.value = [{ id: 'content', title: '正文', blocks: [{ type: 'richText', content: documentFromHtml(value) }] }];
  writingFromEditor = false;
}

watch(() => model.value, () => {
  if (!writingFromEditor) html.value = cmsRichTextHtml(documentOf(model.value));
}, { deep: true, flush: 'sync' });

const editorConfig = {
  height: 520,
  menubar: 'file edit view insert format tools table help',
  block_formats: '正文=p; 标题 1=h1; 标题 2=h2; 标题 3=h3; 引用=blockquote; 代码块=pre',
  plugins: 'advlist anchor autolink charmap code fullscreen help image insertdatetime link lists preview searchreplace table visualblocks wordcount',
  toolbar: 'undo redo | blocks | bold italic underline strikethrough | bullist numlist blockquote | link image table | removeformat code preview fullscreen',
  toolbar_mode: 'sliding',
  branding: false,
  promotion: false,
  paste_data_images: false,
  automatic_uploads: true,
  images_upload_handler: async (blobInfo: { blob: () => Blob }): Promise<string> => {
    try {
      const blob = blobInfo.blob();
      const file = new File([blob], `cms-image-${Date.now()}.${blob.type.split('/')[1] || 'png'}`, { type: blob.type || 'image/png' });
      const asset = await store.uploadAsset(file);
      message.success('图片已上传，发布文章后对外可见');
      return asset.url;
    } catch (error) {
      const detail = (error as Error).message || '图片上传失败';
      message.error(detail);
      throw new Error(detail);
    }
  },
  content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 15px; line-height: 1.75; color: #1d2939; } img { max-width: 100%; height: auto; border-radius: 8px; } blockquote { margin-left: 0; padding-left: 12px; border-left: 3px solid #6657e8; color: #667085; }',
};
</script>

<style scoped>
.rich-editor{overflow:hidden;border:1px solid var(--admin-border,#d0d5dd);border-radius:8px}.license-alert{margin:12px 12px 0}.rich-editor :deep(.tox-tinymce){border:0;border-radius:0}.rich-editor :deep(.tox-statusbar){border-top-color:var(--admin-border,#d0d5dd)}
</style>
